import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Receipt as STCIcon,
  EditNote as EditIcon,
} from '@mui/icons-material';
import { employeesAPI, stcAPI } from '../../../services/api';
import { Employee, MOTIF_SORTIE_OPTIONS } from '../../../types/employee';
import { STC, STCEmployeeInfo } from '../../../types/stc';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import ColumnFilter from '../../../components/ColumnFilter';
import STCFormDialog from './STCFormDialog';

const HR_COLOR = '#00897B';

// Column-header filters — map to the /api/employees query params the STC list
// (INACTIF employees) is fetched with. date_sortie / motif_sortie were added to
// the employees endpoint for this table.
const STC_FILTER_SCHEMA = {
  matricule:    { type: 'text',      param: 'matricule' },
  nom_complet:  { type: 'text',      param: 'nom_complet' },
  cin:          { type: 'text',      param: 'cin' },
  date_sortie:  { type: 'dateRange', paramFrom: 'date_sortie_from', paramTo: 'date_sortie_to' },
  motif_sortie: { type: 'select',    param: 'motif_sortie' },
  stc_status:   { type: 'select',    param: 'has_stc' },
} as const;

// STC established / not-established — maps to the employees endpoint's has_stc bool.
const STC_STATUS_OPTIONS = [
  { value: 'true', label: 'Établi' },
  { value: 'false', label: 'Non établi' },
];

const STCManagement: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [stcByEmployee, setStcByEmployee] = useState<Record<number, STC>>({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formEmployee, setFormEmployee] = useState<STCEmployeeInfo | null>(null);

  const { showError } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const { filters: colFilters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    STC_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, stcRes] = await Promise.all([
        employeesAPI.getAll({
          page: page + 1,
          size: rowsPerPage,
          statut: 'INACTIF',
          search: search || undefined,
          ...toQueryParams(),
        }),
        // Same population the table lists, so the KPI cannot claim settlements
        // the page has no row for.
        stcAPI.getAll('INACTIF'),
      ]);
      setEmployees(empRes.items);
      setTotalCount(empRes.total);
      const map: Record<number, STC> = {};
      stcRes.items.forEach((s) => { map[s.employee_id] = s; });
      setStcByEmployee(map);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // toQueryParams is derived from colFilters, which is in the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, colFilters, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const openForm = (employee: Employee) => {
    setFormEmployee({
      id: employee.id,
      matricule: employee.matricule,
      nom_complet: employee.nom_complet,
      cin: employee.cin,
      numero_cnss: employee.numero_cnss,
      fonction: employee.fonction,
      date_sortie: employee.date_sortie,
      motif_sortie: employee.motif_sortie,
      salaire_base: employee.salaire_base,
      date_embauche: employee.date_embauche,
    });
    setFormOpen(true);
  };

  const printStc = (stcId: number) => {
    openPreview({
      getBlob: () => stcAPI.getPDFBlob(stcId),
      fileName: `solde_tout_compte_${stcId}.pdf`,
    });
  };

  const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '-');

  const savedCount = Object.keys(stcByEmployee).length;

  return (
    <Box sx={{ p: 0 }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: HR_COLOR, color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <STCIcon />
                <Typography variant="h6">{totalCount}</Typography>
              </Box>
              <Typography variant="body2">Employés sortis (inactifs)</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: '#455A64', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <STCIcon />
                <Typography variant="h6">{savedCount}</Typography>
              </Box>
              <Typography variant="body2">STC établis</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Rechercher un employé..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
            sx={{ minWidth: 250 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          {hasActive() && (
            <Button size="small" onClick={reset} sx={{ color: 'text.secondary' }}>
              Effacer les filtres
            </Button>
          )}
          <Tooltip title="Actualiser">
            <IconButton onClick={fetchData} sx={{ color: HR_COLOR }}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>

        <TableContainer sx={getModuleTableTheme('hr')}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Matricule
                    <ColumnFilter type="text" value={colFilters.matricule} onChange={(v) => update('matricule', v)} label="matricule" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Employé
                    <ColumnFilter type="text" value={colFilters.nom_complet} onChange={(v) => update('nom_complet', v)} label="nom" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    CIN
                    <ColumnFilter type="text" value={colFilters.cin} onChange={(v) => update('cin', v)} label="CIN" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date sortie
                    <ColumnFilter type="dateRange" value={colFilters.date_sortie} onChange={(v) => update('date_sortie', v)} label="date de sortie" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Motif
                    <ColumnFilter type="select" value={colFilters.motif_sortie} onChange={(v) => update('motif_sortie', v)} options={MOTIF_SORTIE_OPTIONS} label="motif" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    STC
                    <ColumnFilter type="select" value={colFilters.stc_status} onChange={(v) => update('stc_status', v)} options={STC_STATUS_OPTIONS} label="statut STC" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell align="center">Net à payer</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center">Chargement...</TableCell></TableRow>
              ) : employees.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center">Aucun employé sorti (inactif)</TableCell></TableRow>
              ) : (
                employees.map((employee) => {
                  const stc = stcByEmployee[employee.id];
                  return (
                    <TableRow key={employee.id} hover>
                      <TableCell><Typography variant="body2" fontWeight={500}>{employee.matricule}</Typography></TableCell>
                      <TableCell><Typography variant="body2" fontWeight={500}>{employee.nom_complet}</Typography></TableCell>
                      <TableCell>{employee.cin || '-'}</TableCell>
                      <TableCell>{formatDate(employee.date_sortie)}</TableCell>
                      <TableCell>{employee.motif_sortie || '-'}</TableCell>
                      <TableCell>
                        {stc ? (
                          <Chip label={stc.ref_stc} size="small" sx={{ bgcolor: HR_COLOR, color: 'white', fontWeight: 'bold' }} />
                        ) : (
                          <Chip label="Non établi" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {stc ? <b>{Number(stc.net_a_payer).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b> : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={stc ? 'Modifier le STC' : 'Saisir le STC'}>
                            <IconButton size="small" onClick={() => openForm(employee)} sx={{ color: HR_COLOR }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={stc ? 'Imprimer le reçu' : 'Saisir le STC d\'abord'}>
                            <span>
                              <IconButton size="small" disabled={!stc} onClick={() => stc && printStc(stc.id)} sx={{ color: '#1976D2' }}>
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      {formEmployee && (
        <STCFormDialog
          open={formOpen}
          employee={formEmployee}
          onClose={() => setFormOpen(false)}
          onSaved={fetchData}
          onPrint={printStc}
        />
      )}

      {pdfPreviewElement}
    </Box>
  );
};

export default STCManagement;
